import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import * as signalR from '@microsoft/signalr'
import { authService } from '../servicesGlobais/authService'

interface PendenciasSocketContextType {
  connection: signalR.HubConnection | null
  isConnected: boolean
}

const PendenciasSocketContext = createContext<PendenciasSocketContextType | undefined>(undefined)

interface PendenciasSocketProviderProps {
  children: ReactNode
  usuarioId?: string
}

export function PendenciasSocketProvider({ children, usuarioId }: PendenciasSocketProviderProps) {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!usuarioId || !authService.isAuthenticated()) return

    let cancelled = false
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5160'
    const hubUrl = `${apiUrl.replace('/api', '')}/hubs/pendencias`

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          const token = localStorage.getItem('token')
          return token || ''
        }
      })
      .withAutomaticReconnect()
      .build()

    newConnection.onreconnecting(() => {
      if (!cancelled) setIsConnected(false)
    })

    newConnection.onreconnected(() => {
      if (!cancelled) {
        setIsConnected(true)
        newConnection.invoke('JoinGroup', usuarioId).catch(() => {})
      }
    })

    newConnection.onclose(() => {
      if (!cancelled) setIsConnected(false)
    })

    setConnection(newConnection)

    const startPromise = newConnection
      .start()
      .then(() => {
        if (!cancelled) {
          setIsConnected(true)
          newConnection.invoke('JoinGroup', usuarioId).catch(() => {})
        }
      })
      .catch(() => {})

    return () => {
      cancelled = true
      if (newConnection) {
        newConnection.invoke('LeaveGroup', usuarioId).catch(() => {})
        const doStop = () => {
          if (newConnection.state !== signalR.HubConnectionState.Disconnected) {
            newConnection.stop().catch(() => {})
          }
        }
        Promise.race([
          startPromise,
          new Promise((resolve) => setTimeout(resolve, 500))
        ]).finally(() => {
          doStop()
        })
      }
    }
  }, [usuarioId])

  return (
    <PendenciasSocketContext.Provider value={{ connection, isConnected }}>
      {children}
    </PendenciasSocketContext.Provider>
  )
}

export function usePendenciasSocket() {
  const context = useContext(PendenciasSocketContext)
  if (context === undefined) {
    throw new Error('usePendenciasSocket deve ser usado dentro de PendenciasSocketProvider')
  }
  return context
}

